import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';

// ── Role helpers (exported so AssetLibrary.jsx can import them) ───────────────

const UPLOAD_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];
const ADMIN_ROLES  = ['Admin', 'Power User', 'Power_User'];
const PROP_TYPES   = ['costume', 'branded merch', 'set dressing', 'hand prop', 'food', 'other'];

export function userCanUpload(user) { return user?.roles?.some(r => UPLOAD_ROLES.includes(r)); }
export function userIsAdmin(user)   { return user?.roles?.some(r => ADMIN_ROLES.includes(r)); }

export { PROP_TYPES };

function fmtBytes(b) {
  if (!b) return '—';
  const n = Number(b);
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const TAG_META = [
  { key: 'objects', label: 'Objects', bg: 'rgba(139,109,255,0.12)', color: 'var(--violet)' },
  { key: 'brands',  label: 'Brands',  bg: 'rgba(212,168,85,0.13)',  color: 'var(--s-filmed-t)' },
  { key: 'themes',  label: 'Themes',  bg: 'rgba(77,170,112,0.12)',  color: 'var(--s-approved-t)' },
  { key: 'colors',  label: 'Colors',  bg: 'rgba(84,153,212,0.12)',  color: 'var(--s-editing-t)' },
];

// ── Tag editor (admin inline form) ────────────────────────────────────────────

function TagEditor({ draft, onChange, onSave, onCancel, saving }) {
  function updateArr(key, idx, val) {
    const arr = [...(draft[key] || [])];
    arr[idx] = val;
    onChange({ ...draft, [key]: arr });
  }
  function addItem(key)         { onChange({ ...draft, [key]: [...(draft[key] || []), ''] }); }
  function removeItem(key, idx) { onChange({ ...draft, [key]: (draft[key] || []).filter((_, i) => i !== idx) }); }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '14px 0' }}>
      <div style={{ fontSize: 10, color: 'var(--tx-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Edit Tags</div>

      <div>
        <label style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'block', marginBottom: 5 }}>Prop Type</label>
        <select className="vc-input" value={draft.prop_type || ''} onChange={e => onChange({ ...draft, prop_type: e.target.value })} style={{ fontSize: 12 }}>
          <option value="">—</option>
          {PROP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {['objects', 'brands', 'themes', 'colors'].map(key => (
        <div key={key}>
          <label style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'block', marginBottom: 6, textTransform: 'capitalize' }}>{key}</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(draft[key] || []).map((val, i) => (
              <div key={i} style={{ display: 'flex', gap: 5 }}>
                <input className="vc-input" value={val} onChange={e => updateArr(key, i, e.target.value)} style={{ flex: 1, fontSize: 12 }} />
                <button onClick={() => removeItem(key, i)} style={{ background: 'none', border: 'none', color: 'var(--tx-faint)', cursor: 'pointer', fontSize: 14, padding: '0 6px', lineHeight: 1 }}>✕</button>
              </div>
            ))}
            <button
              onClick={() => addItem(key)}
              style={{ background: 'none', border: '1px dashed var(--border-mid)', color: 'var(--tx-muted)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}
            >
              + Add {key.replace(/s$/, '')}
            </button>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={onSave} disabled={saving} style={{ fontSize: 12, padding: '7px 16px' }}>
          {saving ? 'Saving…' : 'Save Tags'}
        </button>
        <button className="btn-ghost" onClick={onCancel} style={{ fontSize: 12, padding: '7px 14px' }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Asset Card Modal ──────────────────────────────────────────────────────────

export default function AssetCardModal({ assetId, onClose, onArchived, onDeleted, onFolderChanged, user, folders }) {
  const [asset,          setAsset]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [notes,          setNotes]          = useState('');
  const [notesSaved,     setNotesSaved]     = useState(false);
  const [downloading,    setDownloading]    = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [archiving,      setArchiving]      = useState(false);
  const [deleteConfirm,  setDeleteConfirm]  = useState(false);
  const [deleting,       setDeleting]       = useState(false);
  const [retrying,        setRetrying]        = useState(false);
  const [editingTags,     setEditingTags]     = useState(false);
  const [tagDraft,        setTagDraft]        = useState({});
  const [tagSaving,       setTagSaving]       = useState(false);
  const [movingFolder,    setMovingFolder]    = useState(false);
  const [reexamineOpen,   setReexamineOpen]   = useState(false);
  const [reexamineHint,   setReexamineHint]   = useState('');
  const [reexamining,     setReexamining]     = useState(false);
  const [reexamineResult, setReexamineResult] = useState(null); // 'success' | 'error'
  const retryTimerRef = useRef(null);

  const canUp      = userCanUpload(user);
  const isAdm      = userIsAdmin(user);
  const isEditor   = user?.roles?.includes('Editor');
  const isInternal = !user?.isExternal;
  const canMoveFolder = canUp && isInternal;
  // Backend enforces the editor-vs-editor + internal/external wall on delete.
  const canDelete  = isAdm || isEditor;

  useEffect(() => {
    api.get(`/api/assets/${assetId}`)
      .then(r => { setAsset(r.data); setNotes(r.data.notes || ''); setTagDraft(r.data.aiTags || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => { if (retryTimerRef.current) clearInterval(retryTimerRef.current); };
  }, [assetId]);

  async function handleNoteSave() {
    try {
      const r = await api.patch(`/api/assets/${assetId}`, { notes });
      setAsset(a => ({ ...a, notes: r.data.notes }));
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch {}
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const r = await api.get(`/api/assets/${assetId}/download`);
      const a = document.createElement('a');
      a.href = r.data.url; a.download = asset?.fileName || 'asset'; a.click();
    } catch {} finally { setDownloading(false); }
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await api.patch(`/api/assets/${assetId}/archive`);
      onArchived?.(); onClose();
    } catch {} finally { setArchiving(false); }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete(`/api/assets/${assetId}`);
      onDeleted?.(); onClose();
    } catch {} finally { setDeleting(false); }
  }

  async function handleRetry() {
    setRetrying(true);
    try {
      await api.post(`/api/assets/${assetId}/reanalyze`);
      setAsset(a => ({ ...a, analysisStatus: 'pending' }));
      retryTimerRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/api/assets/${assetId}`);
          if (r.data.analysisStatus !== 'pending') {
            clearInterval(retryTimerRef.current);
            setAsset(r.data); setTagDraft(r.data.aiTags || {}); setRetrying(false);
          }
        } catch { clearInterval(retryTimerRef.current); setRetrying(false); }
      }, 2000);
    } catch { setRetrying(false); }
  }

  async function handleReexamineGuided() {
    if (!reexamineHint.trim() || reexamining) return;
    setReexamining(true);
    setReexamineResult(null);
    try {
      const r = await api.post(`/api/assets/${assetId}/reanalyze-guided`, { hint: reexamineHint.trim() });
      setAsset(prev => ({ ...prev, ...r.data }));
      setTagDraft(r.data.aiTags || {});
      setReexamineHint('');
      setReexamineOpen(false);
      setReexamineResult('success');
      setTimeout(() => setReexamineResult(null), 3000);
    } catch {
      setReexamineResult('error');
    } finally {
      setReexamining(false);
    }
  }

  async function handleTagSave() {
    setTagSaving(true);
    try {
      const r = await api.patch(`/api/assets/${assetId}`, { aiTags: tagDraft });
      setAsset(a => ({ ...a, aiTags: r.data.aiTags }));
      setEditingTags(false);
    } catch {} finally { setTagSaving(false); }
  }

  async function handleFolderMove(newFolderId) {
    setMovingFolder(true);
    try {
      await api.patch(`/api/assets/${assetId}/move`, { folderId: newFolderId || null });
      setAsset(a => ({ ...a, folderId: newFolderId || null }));
      onFolderChanged?.();
    } catch {} finally { setMovingFolder(false); }
  }

  const tags    = asset?.aiTags || {};
  const hasTags = TAG_META.some(m => tags[m.key]?.length) || tags.prop_type;
  const currentFolderName = asset?.folder?.name
    || folders?.find(f => f.id === asset?.folderId)?.name
    || null;

  const LABEL_STYLE = { fontSize: 10, color: 'var(--tx-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        style={{
          width: 800, maxHeight: '92vh', background: 'var(--bg-card)',
          border: '1px solid var(--border-mid)', borderRadius: 24,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.75), 0 8px 24px rgba(0,0,0,0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="vc-header">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="vc-label">Asset Library</div>
            <div className="vc-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 480, fontSize: 16 }}>
              {asset?.fileName || '…'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {asset?.analysisStatus === 'pending' && (
              <span style={{ fontSize: 11, color: 'var(--violet)', background: 'var(--violet-soft)', padding: '3px 9px', borderRadius: 'var(--radius-pill)', fontWeight: 600 }}>
                Analyzing…
              </span>
            )}
            {asset?.analysisStatus === 'failed' && (
              <span style={{ fontSize: 11, color: 'var(--s-incomplete-t)', background: 'var(--s-incomplete)', padding: '3px 9px', borderRadius: 'var(--radius-pill)', fontWeight: 600 }}>
                Analysis failed
              </span>
            )}
            <button className="vc-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: 'var(--tx-faint)', fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', minHeight: 0 }}>
            {/* Left: full-size image */}
            <div style={{
              width: 340, flexShrink: 0, background: 'var(--bg-editor)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {asset?.cloudfrontUrl
                ? <img
                    src={asset.cloudfrontUrl}
                    alt={asset.fileName}
                    loading="lazy"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                  />
                : <span style={{ color: 'var(--tx-faint)', fontSize: 13 }}>No image</span>}
            </div>

            {/* Right: metadata */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Description */}
              {asset?.aiDescription && (
                <div>
                  <div style={LABEL_STYLE}>Description</div>
                  <div style={{ fontSize: 13, color: 'var(--tx-secondary)', lineHeight: 1.65 }}>{asset.aiDescription}</div>
                </div>
              )}

              {/* Search context (embedding_doc) */}
              {asset?.embeddingDoc && (
                <div>
                  <div style={{ ...LABEL_STYLE, color: 'var(--tx-faint)' }}>Search Context</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-faint)', lineHeight: 1.6, fontStyle: 'italic' }}>{asset.embeddingDoc}</div>
                </div>
              )}

              {/* Tags */}
              {hasTags && !editingTags && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={LABEL_STYLE}>Tags</div>
                    {isAdm && (
                      <button onClick={() => setEditingTags(true)} style={{ background: 'none', border: 'none', color: 'var(--tx-muted)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                        ✎ Edit tags
                      </button>
                    )}
                  </div>

                  {tags.prop_type && (
                    <div style={{ marginBottom: 10 }}>
                      <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 8 }}>Prop Type</span>
                      <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 'var(--radius-pill)', background: 'var(--violet-soft)', color: 'var(--violet)', fontWeight: 600 }}>
                        {tags.prop_type}
                      </span>
                    </div>
                  )}

                  {TAG_META.filter(m => tags[m.key]?.length).map(({ key, label, bg, color }) => (
                    <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                      <span style={{ fontSize: 10, color: 'var(--tx-faint)', width: 52, flexShrink: 0, marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                        {label}
                      </span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {tags[key].map((t, i) => (
                          <span key={i} style={{ fontSize: 10, padding: '3px 9px', borderRadius: 'var(--radius-pill)', background: bg, color, fontWeight: 600 }}>
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tag editor */}
              {editingTags && isAdm && (
                <TagEditor
                  draft={tagDraft}
                  onChange={setTagDraft}
                  onSave={handleTagSave}
                  onCancel={() => setEditingTags(false)}
                  saving={tagSaving}
                />
              )}

              {/* Info */}
              <div>
                <div style={LABEL_STYLE}>Info</div>
                {[
                  { label: 'Project',   value: asset?.client?.name || <span style={{ color: 'var(--tx-faint)' }}>—</span> },
                  {
                    label: 'Folder',
                    value: canMoveFolder ? (
                      <select
                        className="vc-input"
                        value={asset?.folderId || ''}
                        onChange={e => handleFolderMove(e.target.value || null)}
                        disabled={movingFolder}
                        style={{ fontSize: 12, padding: '2px 6px', minWidth: 130, maxWidth: 200 }}
                      >
                        <option value="">Unassigned</option>
                        {folders?.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    ) : (
                      currentFolderName
                        ? <span>{currentFolderName}</span>
                        : <span style={{ color: 'var(--tx-faint)' }}>Unassigned</span>
                    ),
                  },
                  { label: 'Uploaded', value: asset?.uploader?.name || '—' },
                  { label: 'Date',     value: fmtDate(asset?.createdAt) },
                  { label: 'Size',     value: fmtBytes(asset?.fileSizeBytes) },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', gap: 12, fontSize: 12, marginBottom: 7, alignItems: 'center' }}>
                    <span style={{ color: 'var(--tx-muted)', width: 60, flexShrink: 0 }}>{label}</span>
                    <span style={{ color: 'var(--tx-secondary)' }}>{value}</span>
                  </div>
                ))}
              </div>

              {/* Notes */}
              <div>
                <div style={LABEL_STYLE}>Notes</div>
                {canUp ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea
                      className="vc-input"
                      value={notes}
                      onChange={e => { setNotes(e.target.value); setNotesSaved(false); }}
                      onBlur={handleNoteSave}
                      rows={3}
                      placeholder="Add notes about this asset…"
                      style={{ fontSize: 12 }}
                    />
                    {notesSaved && <span style={{ fontSize: 11, color: 'var(--s-approved-t)' }}>Saved</span>}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--tx-secondary)', lineHeight: 1.55 }}>
                    {asset?.notes || <span style={{ color: 'var(--tx-faint)', fontStyle: 'italic' }}>No notes</span>}
                  </div>
                )}
              </div>

              {/* Guided Re-examination — Admin / Power User only */}
              {isAdm && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <button
                    onClick={() => { setReexamineOpen(o => !o); setReexamineResult(null); }}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                      display: 'flex', alignItems: 'center', gap: 6,
                      fontSize: 11, fontWeight: 700, color: 'var(--tx-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{reexamineOpen ? '▾' : '▸'}</span>
                    Re-examine with Clio
                  </button>

                  {reexamineOpen && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--tx-faint)', lineHeight: 1.5 }}>
                        Describe what Clio got wrong — e.g. "This is a pirate hat, not a wizard hat" or "The brand is Snyder's, not generic."
                      </div>
                      <textarea
                        className="vc-input"
                        value={reexamineHint}
                        onChange={e => setReexamineHint(e.target.value)}
                        rows={3}
                        placeholder="Correction hint…"
                        disabled={reexamining}
                        style={{ fontSize: 12 }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button
                          className="btn-primary"
                          onClick={handleReexamineGuided}
                          disabled={reexamining || !reexamineHint.trim()}
                          style={{ fontSize: 12, padding: '6px 16px' }}
                        >
                          {reexamining ? 'Re-examining…' : 'Re-examine'}
                        </button>
                        {reexamineResult === 'error' && (
                          <span style={{ fontSize: 11, color: 'var(--s-incomplete-t)' }}>Re-examination failed. Try again.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {reexamineResult === 'success' && (
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--s-approved-t)' }}>
                      Analysis updated successfully.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="vc-footer">
            {/* Left actions */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {isAdm && asset?.analysisStatus === 'failed' && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '6px 13px', color: 'var(--violet)', borderColor: 'rgba(139,109,255,0.35)' }}
                  onClick={handleRetry}
                  disabled={retrying}
                >
                  {retrying ? 'Retrying…' : '↺ Retry Analysis'}
                </button>
              )}

              {/* Archive */}
              {canUp && !archiveConfirm && !deleteConfirm && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '6px 13px', color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                  onClick={() => setArchiveConfirm(true)}
                >
                  ⊘ Archive
                </button>
              )}
              {archiveConfirm && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>Archive this asset? It will be hidden from search.</span>
                  <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setArchiveConfirm(false)}>Cancel</button>
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 12, padding: '5px 12px', color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                    onClick={handleArchive}
                    disabled={archiving}
                  >
                    {archiving ? 'Archiving…' : 'Confirm'}
                  </button>
                </div>
              )}

              {/* Delete — Admin, or Editor (own uploads + cross-editor within
                  same internal/external pool — backend enforces the wall). */}
              {canDelete && !deleteConfirm && !archiveConfirm && (
                <button
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '6px 13px', color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.25)' }}
                  onClick={() => setDeleteConfirm(true)}
                >
                  Delete
                </button>
              )}
              {deleteConfirm && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--tx-muted)' }}>Permanently delete this asset? This cannot be undone.</span>
                  <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => setDeleteConfirm(false)}>Cancel</button>
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 12, padding: '5px 12px', color: 'var(--s-incomplete-t)', borderColor: 'rgba(212,85,85,0.3)' }}
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting…' : 'Delete Forever'}
                  </button>
                </div>
              )}
            </div>

            {/* Right actions */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 14px' }} onClick={handleDownload} disabled={downloading}>
                {downloading ? 'Preparing…' : '⬇ Download'}
              </button>
              <button className="btn-ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
